import React, { useMemo, useState } from 'react';
import { Alert, Box, Button, Checkbox, FormControlLabel, FormGroup, Snackbar } from '@mui/material';
import {
  IconPrinter,
  IconKey,
  IconLock,
  IconTag,
  IconUser,
  IconEye,
  IconEyeOff,
} from '@tabler/icons-react';
import type { CustomerIndexItemDto } from '@techgitter/contracts';
import { printLabel } from '../services/printApi';
import { LABEL_CATALOG } from '../config/labelCatalog';
import {
  buildPrintLabelRequest,
  normalizeCopies,
  type LabelKind,
} from '../utils/buildPrintLabelRequest';
import { CustomerLookupAutocomplete } from '../components/check-in/CustomerLookupAutocomplete';
import { FormField } from '../components/panel/FormField';
import { SmartSelect, type SmartSelectOption } from '../components/dropdown';
import { Chip } from '../components/chip';
import { usePrintLabelPrinters } from '../hooks/usePrintLabelPrinters';
import {
  loadPrinterDefaults,
  parsePrinterTargetKey,
  printerTargetKey,
  samePrinterTarget,
  savePrinterDefault,
  type PrinterDefaults,
  type PrinterTarget,
} from '../services/printerDefaults';
import { CARD_SX, CHECKBOX_SX, PRIMARY_BTN_SX } from '../styles/designSystem';
import { useCurrentUser } from '../contexts/CurrentUserContext';
import './PrintPage.css';

type SnackState = { open: boolean; message: string; severity: 'success' | 'error' | 'info' };

// Label-type switcher options — a single-select "pick one" rendered as
// radio-option boxes (sm4rt-web-ui §12), each with its Tabler glyph.
const LABEL_TYPES: ReadonlyArray<{ value: LabelKind; label: string; Icon: typeof IconKey }> = [
  { value: 'pin', label: 'PIN', Icon: IconKey },
  { value: 'password', label: 'Password', Icon: IconLock },
  { value: 'preformatted', label: 'Preformatted', Icon: IconTag },
  { value: 'customer', label: 'Customer', Icon: IconUser },
];

export const PrintPage: React.FC = () => {
  const [kind, setKind] = useState<LabelKind>('pin');
  const [pin, setPin] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState(LABEL_CATALOG[0]?.document ?? '');
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [pickedCustomer, setPickedCustomer] = useState<CustomerIndexItemDto | null>(null);
  const [custShowMobile, setCustShowMobile] = useState(true);
  const [custShowEmail, setCustShowEmail] = useState(true);
  const [custNote, setCustNote] = useState('');
  const [copies, setCopies] = useState(1);
  const [printing, setPrinting] = useState(false);
  const [snack, setSnack] = useState<SnackState>({ open: false, message: '', severity: 'success' });

  // Printer routing. `defaults` is what this browser has saved per label type;
  // `overrides` is this session's picks. A kind absent from `overrides` follows
  // its default — which is why the value is `PrinterTarget | null` and the
  // lookup distinguishes `undefined` (follow the default) from `null` (the user
  // cleared the field, so let the API resolve the site default).
  const { printers, isLoading: printersLoading, error: printersError } = usePrintLabelPrinters();
  const [printerDefaults, setPrinterDefaults] = useState<PrinterDefaults>(loadPrinterDefaults);
  const [printerOverrides, setPrinterOverrides] = useState<
    Partial<Record<LabelKind, PrinterTarget | null>>
  >({});

  // This browser's saved choice for the label type wins; the signed-in user's
  // profile default (Settings page) only fills in when the browser has none —
  // where a label comes out is still a property of where the operator stands.
  const currentUser = useCurrentUser();
  const browserDefaultPrinter = printerDefaults[kind] ?? null;
  const profileDefaultPrinter = currentUser?.preferences.defaultLabelPrinter ?? null;
  const defaultPrinter = browserDefaultPrinter ?? profileDefaultPrinter;
  const isProfileDefault = !browserDefaultPrinter && Boolean(profileDefaultPrinter);
  const selectedPrinter =
    kind in printerOverrides ? printerOverrides[kind] ?? null : defaultPrinter;
  const isDefaultPrinter = Boolean(selectedPrinter) && samePrinterTarget(selectedPrinter, defaultPrinter);
  const kindLabel = LABEL_TYPES.find((t) => t.value === kind)?.label ?? 'this';

  const selectedEntry = useMemo(
    () => LABEL_CATALOG.find((e) => e.document === selectedDoc) ?? null,
    [selectedDoc],
  );

  const labelOptions = useMemo<SmartSelectOption[]>(
    () => LABEL_CATALOG.map((entry) => ({ value: entry.document, label: entry.title })),
    [],
  );

  // One option per label lane. The role is the operator-facing name (it is what
  // Admin › Printers shows); the site is appended only when more than one is in
  // play, since with a single site it's noise on every row.
  const printerOptions = useMemo<SmartSelectOption[]>(() => {
    const multiSite = new Set(printers.map((p) => p.siteId)).size > 1;
    const options = printers.map((printer) => ({
      value: printerTargetKey(printer),
      label: multiSite ? `${printer.role} · ${printer.siteId}` : printer.role,
    }));
    // A saved default still routes the job even when the lane list didn't load
    // or the lane has since been removed from the fleet — so carry it as an
    // option rather than leaving the field blank while printing elsewhere.
    if (selectedPrinter && !options.some((o) => o.value === printerTargetKey(selectedPrinter))) {
      options.unshift({
        value: printerTargetKey(selectedPrinter),
        label: `${selectedPrinter.role} (saved)`,
      });
    }
    return options;
  }, [printers, selectedPrinter]);

  const handlePrinterChange = (value: string | null) =>
    setPrinterOverrides((current) => ({ ...current, [kind]: parsePrinterTargetKey(value) }));

  const handleSetDefaultPrinter = () => {
    if (!selectedPrinter) return;
    setPrinterDefaults(savePrinterDefault(kind, selectedPrinter));
    setSnack({
      open: true,
      message: `${kindLabel} labels now default to ${selectedPrinter.role}`,
      severity: 'success',
    });
  };

  const handleClearDefaultPrinter = () => {
    setPrinterDefaults(savePrinterDefault(kind, null));
    setSnack({
      open: true,
      message: `Default printer cleared for ${kindLabel} labels`,
      severity: 'success',
    });
  };

  // The customer record's contact values, if any — drive the include checkboxes
  // (a field with no value has nothing to include, so its box is disabled).
  const customerMobile = (pickedCustomer?.mobile || pickedCustomer?.phone || '').trim();
  const customerEmail = (pickedCustomer?.email || '').trim();

  const setField = (name: string, value: string) =>
    setFieldValues((current) => ({ ...current, [name]: value }));

  const handlePrint = async () => {
    const built = buildPrintLabelRequest({
      kind,
      pin,
      password,
      preformatted: selectedEntry,
      fieldValues,
      customer: pickedCustomer,
      customerShowMobile: custShowMobile,
      customerShowEmail: custShowEmail,
      customerNote: custNote,
      copies,
      printer: selectedPrinter,
    });
    if (!built.ok) {
      setSnack({ open: true, message: built.error, severity: 'error' });
      return;
    }
    setPrinting(true);
    try {
      const result = await printLabel(built.request);
      if (result.skipped) {
        setSnack({
          open: true,
          message: `Print skipped${result.reason ? `: ${result.reason}` : ''}`,
          severity: 'info',
        });
      } else {
        setSnack({ open: true, message: 'Label sent to printer', severity: 'success' });
      }
    } catch (err) {
      setSnack({
        open: true,
        message: err instanceof Error ? err.message : 'Failed to print label',
        severity: 'error',
      });
    } finally {
      setPrinting(false);
    }
  };

  return (
    <div className="print-page">
      <div className="print-page__inner">
        {/* Nav names the page (§5) — no in-page title; just a recessive lead line. */}
        <p className="print-page__lead">
          Print PINs, passwords, preformatted and customer labels to the counter label printer
          (90mm).
        </p>

        <div className="print-page__grid">
          {/* Form card */}
          <Box sx={{ ...CARD_SX, p: 2 }}>
            <div className="print-field-label">Label type</div>
            <div className="print-type-grid">
              {LABEL_TYPES.map(({ value, label, Icon }) => (
                <label
                  key={value}
                  className={`radio-option${kind === value ? ' radio-option--selected' : ''}`}
                >
                  <input
                    type="radio"
                    className="print-type__input"
                    name="label-kind"
                    checked={kind === value}
                    onChange={() => setKind(value)}
                  />
                  <span className="radio-option__icon">
                    <Icon size={16} stroke={1.5} />
                  </span>
                  <span className="radio-option__label">{label}</span>
                </label>
              ))}
            </div>

            <div className="print-form">
              {kind === 'pin' && (
                <FormField label="PIN">
                  <input
                    value={pin}
                    onChange={(e) => setPin(e.target.value)}
                    placeholder="e.g. 1234"
                    autoFocus
                  />
                </FormField>
              )}

              {kind === 'password' && (
                <FormField label="Password">
                  <div className="print-input-action">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="e.g. hunter2"
                      autoFocus
                    />
                    <button
                      type="button"
                      className="print-input-action__btn"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      onClick={() => setShowPassword((s) => !s)}
                    >
                      {showPassword ? (
                        <IconEyeOff size={18} stroke={1.5} />
                      ) : (
                        <IconEye size={18} stroke={1.5} />
                      )}
                    </button>
                  </div>
                </FormField>
              )}

              {kind === 'preformatted' && (
                <>
                  <SmartSelect
                    label="Label"
                    value={selectedDoc || null}
                    options={labelOptions}
                    onChange={(value) => {
                      setSelectedDoc(value ?? '');
                      setFieldValues({});
                    }}
                  />
                  {selectedEntry?.description && (
                    <p className="print-hint">{selectedEntry.description}</p>
                  )}

                  {selectedEntry?.fields.map((field) => (
                    <FormField key={field.name} label={field.label + (field.required ? ' *' : '')}>
                      <input
                        value={fieldValues[field.name] ?? ''}
                        onChange={(e) => setField(field.name, e.target.value)}
                        placeholder={field.placeholder}
                        maxLength={field.maxLength}
                      />
                    </FormField>
                  ))}
                </>
              )}

              {kind === 'customer' && (
                <div className="print-customer">
                  <CustomerLookupAutocomplete onPick={setPickedCustomer} />
                  {pickedCustomer && (
                    <>
                      <p className="print-hint">
                        Selected:{' '}
                        {pickedCustomer.businessName ||
                          pickedCustomer.displayName ||
                          pickedCustomer.fullName}
                      </p>
                      <div className="print-field-label print-field-label--sub">Fields to include</div>
                      <FormGroup row>
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              sx={CHECKBOX_SX}
                              checked={custShowMobile && Boolean(customerMobile)}
                              onChange={(e) => setCustShowMobile(e.target.checked)}
                            />
                          }
                          label="Mobile"
                          disabled={!customerMobile}
                          sx={{ '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              sx={CHECKBOX_SX}
                              checked={custShowEmail && Boolean(customerEmail)}
                              onChange={(e) => setCustShowEmail(e.target.checked)}
                            />
                          }
                          label="Email"
                          disabled={!customerEmail}
                          sx={{ '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                        />
                      </FormGroup>
                      <FormField label="Note (optional)">
                        <textarea
                          value={custNote}
                          onChange={(e) => setCustNote(e.target.value)}
                          placeholder="e.g. Left device + charger"
                          rows={2}
                          maxLength={120}
                        />
                      </FormField>
                    </>
                  )}
                </div>
              )}

              {/* Printer + copies — the routing row, shared by every label type
                  but remembered per type (each has its own saved default). */}
              <div className="print-field-row">
                <div className="print-field--printer">
                  <SmartSelect
                    label="Printer"
                    value={selectedPrinter ? printerTargetKey(selectedPrinter) : null}
                    options={printerOptions}
                    onChange={handlePrinterChange}
                    placeholder={printersLoading ? 'Loading printers…' : 'Site default printer'}
                    disabled={printersLoading || printerOptions.length === 0}
                  />
                </div>
                <div className="print-field--copies">
                  <FormField label="Copies">
                    <input
                      type="number"
                      min={1}
                      step={1}
                      value={copies}
                      onChange={(e) => setCopies(normalizeCopies(Number(e.target.value)))}
                    />
                  </FormField>
                </div>
              </div>

              <div className="print-default-row">
                {isDefaultPrinter && isProfileDefault ? (
                  <>
                    <Chip tone="info">Your default label printer</Chip>
                    <button type="button" className="print-link-btn" onClick={handleSetDefaultPrinter}>
                      Pin to this browser for {kindLabel} labels
                    </button>
                  </>
                ) : isDefaultPrinter ? (
                  <>
                    <Chip tone="info">Default for {kindLabel} labels</Chip>
                    <button
                      type="button"
                      className="print-link-btn print-link-btn--quiet"
                      onClick={handleClearDefaultPrinter}
                    >
                      Clear default
                    </button>
                  </>
                ) : selectedPrinter ? (
                  <button type="button" className="print-link-btn" onClick={handleSetDefaultPrinter}>
                    Set as default for {kindLabel} labels
                  </button>
                ) : defaultPrinter ? (
                  <>
                    <p className="print-hint">
                      Using the site default this time — {kindLabel} labels normally go to{' '}
                      {defaultPrinter.role}
                      {isProfileDefault ? ' (your default, set in Settings)' : ''}.
                    </p>
                    {!isProfileDefault && (
                      <button
                        type="button"
                        className="print-link-btn print-link-btn--quiet"
                        onClick={handleClearDefaultPrinter}
                      >
                        Clear default
                      </button>
                    )}
                  </>
                ) : (
                  <p className="print-hint">
                    {printersError
                      ? 'Printer list unavailable — printing to the site default.'
                      : printersLoading
                        ? 'Loading printers…'
                        : printerOptions.length === 0
                          ? 'No label printers configured — printing to the site default.'
                          : 'Pick a printer to save it as the default for this label type.'}
                  </p>
                )}
              </div>

              <Box>
                <Button
                  variant="contained"
                  startIcon={<IconPrinter size={18} stroke={1.5} />}
                  onClick={handlePrint}
                  disabled={printing || (kind === 'customer' && !pickedCustomer)}
                  sx={PRIMARY_BTN_SX}
                >
                  {printing ? 'Printing…' : 'Print label'}
                </Button>
              </Box>
            </div>
          </Box>

          {/* Preview card */}
          <Box sx={{ ...CARD_SX, p: 2 }}>
            <div className="print-field-label">Preview · 90mm (approximate)</div>
            <div className="label-preview" aria-label="Label preview">
              <LabelPreview
                kind={kind}
                pin={pin}
                password={password}
                showPassword={showPassword}
                entry={selectedEntry}
                fieldValues={fieldValues}
                pickedCustomer={pickedCustomer}
                custShowMobile={custShowMobile}
                custShowEmail={custShowEmail}
                custNote={custNote}
              />
            </div>
            <p className="print-hint print-preview__destination">
              Prints to {selectedPrinter ? selectedPrinter.role : 'the site default printer'}
            </p>
          </Box>
        </div>
      </div>

      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

interface LabelPreviewProps {
  kind: LabelKind;
  pin: string;
  password: string;
  showPassword: boolean;
  entry: (typeof LABEL_CATALOG)[number] | null;
  fieldValues: Record<string, string>;
  pickedCustomer: CustomerIndexItemDto | null;
  custShowMobile: boolean;
  custShowEmail: boolean;
  custNote: string;
}

const LabelPreview: React.FC<LabelPreviewProps> = ({
  kind,
  pin,
  password,
  showPassword,
  entry,
  fieldValues,
  pickedCustomer,
  custShowMobile,
  custShowEmail,
  custNote,
}) => {
  if (kind === 'pin') {
    return (
      <>
        <div className="label-preview__caption">PIN</div>
        <div className="label-preview__value label-preview__value--mono">{pin || '— — — —'}</div>
      </>
    );
  }
  if (kind === 'password') {
    const shown = password ? (showPassword ? password : '•'.repeat(password.length)) : '— — — —';
    return (
      <>
        <div className="label-preview__caption">PASSWORD</div>
        <div className="label-preview__value label-preview__value--mono">{shown}</div>
      </>
    );
  }
  if (kind === 'customer') {
    if (!pickedCustomer) {
      return <div className="label-preview__caption">No customer selected</div>;
    }
    const c = pickedCustomer;
    const name = c.businessName || c.displayName || c.fullName || '';
    // Mirror the print payload: a contact shows only when its box is ticked.
    const phone = custShowMobile ? c.mobile || c.phone || '' : '';
    const email = custShowEmail ? c.email || '' : '';
    const note = custNote.trim();
    return (
      <div className="label-preview__fields">
        <div className="label-preview__row">
          <span className="label-preview__row-label">Customer</span>
          <span className="label-preview__row-value">{name || '—'}</span>
        </div>
        {phone && (
          <div className="label-preview__row">
            <span className="label-preview__row-label">Phone</span>
            <span className="label-preview__row-value">{phone}</span>
          </div>
        )}
        {email && (
          <div className="label-preview__row">
            <span className="label-preview__row-label">Email</span>
            <span className="label-preview__row-value">{email}</span>
          </div>
        )}
        <div className="label-preview__row">
          <span className="label-preview__row-label">QR</span>
          <span className="label-preview__row-value">
            /customer/{c.repairShoprCustomerId || c.dataverseCustomerId}
          </span>
        </div>
        {note && (
          <div className="label-preview__row">
            <span className="label-preview__row-label">Note</span>
            <span className="label-preview__row-value">{note}</span>
          </div>
        )}
      </div>
    );
  }
  // preformatted
  if (!entry) return <div className="label-preview__caption">No label selected</div>;
  if (entry.fields.length === 0) {
    return <div className="label-preview__value">{entry.title}</div>;
  }
  return (
    <div className="label-preview__fields">
      {entry.fields.map((field) => (
        <div key={field.name} className="label-preview__row">
          <span className="label-preview__row-label">{field.label}</span>
          <span className="label-preview__row-value">{fieldValues[field.name] || '—'}</span>
        </div>
      ))}
    </div>
  );
};
